import styles from './library-publish-actions.module.css'

import React from 'react'
import CopyToClipboard from 'react-clipboard.js'

import { useTranslation } from '@disco/i18n/use-translation'
import { pushUINotification } from '@disco/utils/notifications'

import type { ClientLibrary, ClientLibraryId } from 'types/client-library'

import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import QRCodeButton from 'components/ui/qr-code-button'
import Switch from 'components/ui/switch'

import { useDebouncedLibraryToggle } from 'hooks/use-debounced-library-toggle'

type Props = {
  libraryId: ClientLibraryId
  library: ClientLibrary | null | undefined
  publishEnabled: boolean
  viewCaption: string
  qrFilename?: string
  successMessage?: string
  onQRAction?: (action: 'download' | 'copy') => void
}

/**
 * Header actions for a client library: live/unpublished toggle, a link to
 * the public page, a QR code and a copy-URL button. While `library` is still
 * loading, only a disabled toggle is shown.
 */
const LibraryPublishActions = ({
  libraryId,
  library,
  publishEnabled,
  viewCaption,
  qrFilename,
  successMessage,
  onQRAction,
}: Props) => {
  const { t } = useTranslation('common')
  const [isLive, toggleIsLive] = useDebouncedLibraryToggle(
    libraryId,
    'is_live',
    Boolean(library?.is_live),
    successMessage,
  )
  const isPublished = publishEnabled && isLive

  return (
    <ButtonContainer className="flc">
      <span className={styles.liveLabel}>
        {isPublished
          ? t('common:live', 'Live')
          : t('common:unpublished', 'Unpublished')}
      </span>
      <Switch
        aria-label={t('common:published', 'Published')}
        disabled={!publishEnabled || !library || Boolean(library.loading)}
        value={isPublished}
        onChange={toggleIsLive}
      />
      {!library?.public_url ? null : (
        <>
          <Button
            caption={viewCaption}
            link={library.public_url}
            linkTarget="_blank"
          />
          <QRCodeButton
            url={library.public_url}
            filename={qrFilename}
            onAction={onQRAction}
          />
          <CopyToClipboard
            component="div"
            data-clipboard-text={library.public_url}
            onSuccess={() =>
              pushUINotification({
                message: t('common:copied', 'Copied!'),
                level: 'success',
              })
            }
          >
            <Button icon="link" tooltip={t('common:copyUrl', 'Copy URL')} />
          </CopyToClipboard>
        </>
      )}
    </ButtonContainer>
  )
}

export default LibraryPublishActions
